import { CalendarDays, Clock, MapPin, Users, ArrowLeft, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const galleryPhotos = [
  { src: "/assets/gallery/simpozion-02.jpeg", alt: "Publicul participant la Simpozionul Omagial George Pomuț", testid: "gallery-photo-2" },
  { src: "/assets/gallery/simpozion-01.jpeg", alt: "Deschiderea simpozionului la Biblioteca Județeană Gheorghe Șincai, Oradea", testid: "gallery-photo-1" },
  { src: "/assets/gallery/simpozion-03.jpeg", alt: "Prezentare în timpul simpozionului dedicat generalului George Pomuț", testid: "gallery-photo-3" },
];

const Event = () => {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = lightbox ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div data-testid="event-page">
      {/* HERO EVENT */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-16 pb-12" data-testid="event-hero">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-soft)] hover:text-[color:var(--gold)]" data-testid="event-back-link">
          <ArrowLeft size={14} /> Înapoi la pagina principală
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 mt-10 items-start">
          <div className="lg:col-span-6 fade-in">
            <div className="eyebrow" data-testid="event-eyebrow">Eveniment · 30 Mai</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-5 leading-[1.05]" data-testid="event-title">
              Simpozion Omagial<br />
              <span className="font-display-italic text-[color:var(--gold)]">General George Pomuț</span>
            </h1>
            <p className="mt-6 text-[color:var(--ink-soft)] text-lg leading-relaxed">
              Un român care a contribuit la edificarea Statelor Unite ale Americii.
              <span className="block mt-2 font-display-italic text-[color:var(--gold)]">(31 mai 1818 — 12 octombrie 1882)</span>
            </p>

            <div className="mt-8 flex items-center gap-5" data-testid="event-flags">
              <div className="relative" data-testid="event-flag-us">
                <img
                  src="/assets/flag-us-250.png"
                  alt="Steagul SUA — 250 de ani de la Declarația de Independență"
                  className="w-32 sm:w-40 h-auto object-contain border border-[color:var(--ink)]/15 shadow-[0_10px_25px_-15px_rgba(15,27,45,0.4)]"
                />
              </div>
              <div className="relative" data-testid="event-flag-ro">
                <img
                  src="/assets/flag-romania.png"
                  alt="Drapelul României"
                  className="w-32 sm:w-40 h-auto object-contain border border-[color:var(--ink)]/15 shadow-[0_10px_25px_-15px_rgba(15,27,45,0.4)]"
                />
              </div>
            </div>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4" data-testid="event-detail-date">
                <CalendarDays className="text-[color:var(--gold)] mt-1" size={22} />
                <div>
                  <div className="eyebrow">Data</div>
                  <div className="font-display text-2xl mt-1">30 Mai</div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="event-detail-time">
                <Clock className="text-[color:var(--gold)] mt-1" size={22} />
                <div>
                  <div className="eyebrow">Ora</div>
                  <div className="font-display text-2xl mt-1">11:00 — 13:00</div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="event-detail-location">
                <MapPin className="text-[color:var(--gold)] mt-1" size={22} />
                <div>
                  <div className="eyebrow">Locație</div>
                  <div className="font-display text-2xl mt-1">Biblioteca Județeană „Gheorghe Șincai”</div>
                  <div className="text-sm text-[color:var(--ink-soft)] mt-1">Calea Armatei Române, nr. 1A, Oradea, jud. Bihor</div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="event-detail-organizer">
                <Users className="text-[color:var(--gold)] mt-1" size={22} />
                <div>
                  <div className="eyebrow">Organizator</div>
                  <div className="font-display text-xl mt-1">Asociația General George Pomuț</div>
                </div>
              </div>
            </div>
          </div>

          {/* POSTER */}
          <div className="lg:col-span-6 fade-in delay-2" data-testid="event-poster-container">
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-28 h-28 border border-[color:var(--gold)]/40" />
              <div className="absolute -bottom-5 -right-5 w-28 h-28 border border-[color:var(--ink)]/25" />
              <div className="relative bg-[color:var(--cream)] p-4 shadow-[0_30px_60px_-30px_rgba(15,27,45,0.35)]">
                <img
                  src="/assets/general-pomut-poster.png"
                  alt="Afiș Simpozion Omagial General George Pomuț — 30 mai, Oradea"
                  className="w-full h-auto object-contain"
                  data-testid="event-poster-image"
                />
                <div className="mt-4 mb-2 px-1 flex items-baseline justify-between">
                  <span className="eyebrow">Afiș Oficial</span>
                  <span className="font-display-italic text-sm text-[color:var(--ink-soft)]">Oradea, 2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="rule max-w-[1280px] mx-auto" />

      {/* PROGRAM */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="event-program">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">Program</div>
            <h2 className="font-display text-4xl mt-4 leading-tight">Desfășurarea simpozionului</h2>
            <p className="mt-6 text-[color:var(--ink-soft)] leading-relaxed text-sm">
              Manifestare culturală dedicată recuperării și valorificării memoriei
              generalului român-american, la împlinirea a 250 de ani de la Declarația
              de Independență a SUA.
            </p>
          </div>

          <div className="lg:col-span-8 space-y-px">
            {[
              { time: "11:00", title: "Cuvânt de deschidere", who: "Dr. Aurel Mircea Romocia — Președintele Asociației" },
              { time: "11:15", title: "George Pomuț — revoluționar și militar", who: "Comunicare istorică" },
              { time: "11:45", title: "Diplomatul care a cumpărat Alaska", who: "Episoade din cariera diplomatică (1874–1878)" },
              { time: "12:15", title: "Memoria lui Pomuț în România și SUA", who: "Recunoaștere postumă, monumente, omagieri" },
              { time: "12:45", title: "Discuții și mesaje ale invitaților", who: "Reprezentanți ai instituțiilor partenere" },
              { time: "13:00", title: "Încheierea lucrărilor", who: "Cocktail și networking" },
            ].map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-6 py-5 border-b border-[color:var(--ink)]/10" data-testid={`program-item-${i}`}>
                <div className="col-span-3 font-display text-2xl text-[color:var(--gold)]">{item.time}</div>
                <div className="col-span-9">
                  <div className="font-display text-xl">{item.title}</div>
                  <div className="text-sm text-[color:var(--ink-soft)] mt-1">{item.who}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTENERI */}
      <section className="ink-bg" data-testid="event-partners">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20">
          <div className="eyebrow text-[color:var(--gold-soft)]">Parteneri & Invitați</div>
          <h2 className="font-display text-4xl text-[color:var(--ivory)] mt-4 max-w-2xl">Alături de noi, în memoria generalului.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              "Biblioteca Județeană „Gheorghe Șincai”",
              "Uniunea Culturală a Românilor din Ungaria",
              "Revista „Foaia Românească”",
              "Ambasada Statelor Unite ale Americii",
              "Comandamentul Centrului de Excelență NATO",
              "Episcopiile Ortodoxă și Greco-Catolică",
            ].map((p, i) => (
              <div key={i} className="border border-white/15 p-6 hover:border-[color:var(--gold-soft)] transition-colors" data-testid={`partner-${i}`}>
                <div className="eyebrow text-[color:var(--gold-soft)]">0{i + 1}</div>
                <div className="font-display text-xl text-[color:var(--ivory)] mt-3 leading-snug">{p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALERIE MOMENTE DIN SIMPOZION */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="event-gallery">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="eyebrow">Momente din sală</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">
              Simpozionul<br />
              <span className="font-display-italic text-[color:var(--gold)]">a avut loc.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[color:var(--ink-soft)] text-[15px] leading-[1.75]">
              Bine reflectată în presă, am avut o reprezentare foarte bună în sală,
              publicul reacționând așa cum ne-am dorit, o participare activă la eveniment.
            </p>
            <p className="mt-4 font-display-italic text-[color:var(--ink)] text-lg leading-snug">
              Mulțumim tuturor! Recunoștință din tot sufletul participanților,
              sponsorilor, cititorilor, celor care ne-au sprijinit de la distanță.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5" data-testid="event-gallery-grid">
          {galleryPhotos.map((photo, i) => (
            <button
              type="button"
              key={photo.src}
              onClick={() => setLightbox(photo)}
              className={`relative overflow-hidden gold-border bg-[color:var(--ink)] group text-left cursor-zoom-in ${i === 1 ? "md:col-span-2" : "md:col-span-1"}`}
              data-testid={photo.testid}
              aria-label={`Mărește: ${photo.alt}`}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="w-full h-56 sm:h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
            </button>
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Vizualizare imagine"
          data-testid="event-gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-[color:var(--gold)] text-white hover:text-[color:var(--ink)] flex items-center justify-center transition-colors"
            aria-label="Închide"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain shadow-2xl cursor-default"
            data-testid="lightbox-image"
          />
        </div>
      )}

      <div className="rule max-w-[1280px] mx-auto" />

      {/* HARTA / ADRESA */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20" data-testid="event-venue">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Locație</div>
            <h2 className="font-display text-4xl mt-4">Biblioteca Județeană „Gheorghe Șincai”</h2>
            <p className="text-[color:var(--ink-soft)] mt-4 leading-relaxed">
              Calea Armatei Române, nr. 1A<br />
              Oradea, județul Bihor, România
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Biblioteca+Județeană+Gheorghe+Șincai+Oradea"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost mt-8"
              data-testid="venue-map-link"
            >
              Deschide harta
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] w-full overflow-hidden gold-border">
              <iframe
                title="Hartă Biblioteca Județeană Gheorghe Șincai Oradea"
                src="https://www.openstreetmap.org/export/embed.html?bbox=21.91%2C47.05%2C21.95%2C47.08&layer=mapnik&marker=47.0667%2C21.9333"
                className="w-full h-full"
                loading="lazy"
                data-testid="venue-map-iframe"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Event;
